import { useEffect, useState } from 'react'

import { api } from '../api/client'
import { useAuth } from '../auth/AuthContext'

/**
 * Whether CENSO MED (the medical devices area, ADR-0027) is active for the
 * current tenant and open to the user's role.
 *
 * Like Eagle, the area is **hidden** from those without it: the area, its menu
 * and its pages appear only when this says `true`. Until the answer arrives it
 * is `false`, so an entry never appears and then disappears. A role with the
 * module hidden gets a 403 from `/medical/status`, which reads as «off». The
 * answer is kept per tenant.
 */
export interface StatoMed {
  attivo: boolean
  /** The answer arrived (until then `attivo` is false by default). */
  pronto?: boolean
  /** CENSO MED is reserved and the user's role does not open it (403): the
   *  medical devices' clinical engineering details are hidden from them. */
  riservato?: boolean
}

const SPENTO: StatoMed = { attivo: false }
const memoria = new Map<string, Promise<StatoMed>>()

function chiedi(tenant: string): Promise<StatoMed> {
  let p = memoria.get(tenant)
  if (!p) {
    p = api
      .medStatus()
      .then((r) => ({ attivo: Boolean(r.licensed), pronto: true }))
      .catch((e: { status?: number }) => {
        // A network error (or a role without the module) must not switch the
        // area off for good: ask again next time.
        memoria.delete(tenant)
        return { attivo: false, pronto: true, riservato: e?.status === 403 }
      })
    memoria.set(tenant, p)
  }
  return p
}

export function useStatoMed(): StatoMed {
  const { principal } = useAuth()
  const tenant = principal?.tenant_id ?? ''
  const [stato, setStato] = useState<StatoMed>(SPENTO)
  useEffect(() => {
    if (!tenant) {
      setStato(SPENTO)
      return
    }
    let vivo = true
    chiedi(tenant).then((v) => {
      if (vivo) setStato(v)
    })
    return () => {
      vivo = false
    }
  }, [tenant])
  return stato
}

export function useMed(): boolean {
  return useStatoMed().attivo
}
